/**
 * Magnus Basic Mode - AI-Powered Enhancement
 * 
 * Handles intelligent navigation and context-aware responses
 * using the Magnus Context Builder
 */

import { chatWithAI } from './geminiService';
import type { MagnusContext } from './magnusContext';
import { buildContextSummary } from './magnusContext';

// Local Message type (same as in MagnusAssistant)
interface Message {
    role: 'user' | 'magnus';
    content: string;
}

/**
 * Handle Basic Mode query with AI
 * Provides intelligent, context-aware navigation and quick answers
 */
export const handleBasicModeQuery = async (
    query: string,
    context: MagnusContext,
    conversationHistory: Message[]
): Promise<{ answer: string; action?: { type: 'navigate'; target: string; label: string } }> => {

    const contextSummary = buildContextSummary(context);

    // Build navigation map for quick reference
    const navigationMap = context.availablePages.map(p =>
        `• ${p.name} → "${p.path}" (${p.category})`
    ).join('\n');

    const systemPrompt = `You are **Magnus Basic**, a friendly and efficient navigation assistant for a reliability engineering platform.

${contextSummary}

AVAILABLE PAGES (for navigation):
${navigationMap}

YOUR ROLE & CAPABILITIES:
1. **Help users navigate** - Tell them exactly where to find features
2. **Provide quick data insights** - Use the data summary above
3. **Suggest next steps** - Based on current page and data
4. **Keep it SHORT** - Maximum 3-4 sentences unless asked for details
5. **Be conversational** - Friendly, not robotic

RESPONSE GUIDELINES:
• If user asks "where is [feature]", tell them the exact menu location
• If user asks about their data, reference the DATA SUMMARY above
• If they're stuck, suggest logical next steps from "Next Steps" list
• Always include a navigation button when relevant

NEXT STEPS FOR CURRENT PAGE:
${context.nextSteps.map((step, i) => `${i + 1}. ${step}`).join('\n')}

If you suggest navigating somewhere, respond in this format at the end:
[NAVIGATE:/path/to/page|Button Label]

User Query: "${query}"

Respond as Magnus Basic would - helpful, concise, and action-oriented.`;

    try {
        // Get AI response
        const rawResponse = await chatWithAI(systemPrompt, { conversationHistory });

        // Parse navigation action if present
        const navigationMatch = rawResponse.match(/\[NAVIGATE:(.*?)\|(.*?)\]/);
        let cleanedResponse = rawResponse;
        let action: { type: 'navigate'; target: string; label: string } | undefined;

        if (navigationMatch) {
            action = {
                type: 'navigate',
                target: navigationMatch[1],
                label: navigationMatch[2]
            };
            // Remove the navigation markup from response
            cleanedResponse = rawResponse.replace(/\[NAVIGATE:.*?\]/, '').trim();
        }

        return {
            answer: cleanedResponse,
            action
        };

    } catch (error) {
        console.error('[Magnus Basic] Error:', error);

        // Fallback response
        return {
            answer: `I'm having trouble thinking right now. Here's what I know: You're on ${context.pageName}. ${context.pagePurpose}. Try asking about where to find a specific feature!`
        };
    }
};

/**
 * Quick action handlers for common queries
 * These bypass AI for instant responses to repeated questions
 */
export const getQuickAction = (query: string, context: MagnusContext): {
    answer: string;
    action?: { type: 'navigate'; target: string; label: string };
} | null => {
    const lower = query.toLowerCase();

    // "Where is..." queries
    if (lower.includes('where') && lower.includes('fmea editor')) {
        return {
            answer: "**FMEA Editor** is in the left sidebar.\n\nLook for the Risk Analysis section → 'FMEA Editor'. This is where you create and edit individual failure modes.",
            action: { type: 'navigate', target: '/fmea-editor', label: 'Go to FMEA Editor' }
        };
    }

    if (lower.includes('where') && (lower.includes('dvp') || lower.includes('test'))) {
        return {
            answer: "**DVP&R** is in the left sidebar.\n\nLook in the Testing & Validation section → 'DVP&R'. This is where you manage test specs and results.",
            action: { type: 'navigate', target: '/dvpr', label: 'Go to DVP&R' }
        };
    }

    if (lower.includes('where') && lower.includes('p-diagram')) {
        return {
            answer: "**P-Diagram** is in the left sidebar.\n\nLook in the System Design section → 'P-Diagram'. You can also use 'FMEA Ideation' to create new ones with AI.",
            action: { type: 'navigate', target: '/p-diagram', label: 'Go to P-Diagram' }
        };
    }

    // Quick data queries
    if (lower.includes('how many') && lower.includes('fmea')) {
        const { dataSummary } = context;
        return {
            answer: `You have **${dataSummary.totalFMEAs} FMEAs** in ${context.projectDisplayName}.\n\n` +
                `• ${dataSummary.activeFMEAs} active\n` +
                `• ${dataSummary.draftFMEAs} draft\n` +
                (dataSummary.highRiskCount > 0 ? `\n⚠️ ${dataSummary.highRiskCount} are high-risk (RPN > 100)` : '')
        };
    }

    if (lower.includes('what') && lower.includes('next')) {
        return {
            answer: `On ${context.pageName}, here's what you can do next:\n\n` +
                context.nextSteps.map((step, i) => `${i + 1}. ${step}`).join('\n')
        };
    }

    // General help
    if (lower === 'help' || lower === 'help me') {
        return {
            answer: `I'm Magnus Basic! I can help you:\n\n` +
                `• Find pages ("where is FMEA Editor?")\n` +
                `• Quick data info ("how many FMEAs?")\n` +
                `• What to do next ("what's next?")\n` +
                `• Navigate the app\n\n` +
                `For deep analysis, toggle Prime mode!`
        };
    }

    return null; // No quick action, use AI
};
